import * as React from "react";
import styled from "styled-components";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {FocusLoss} from "../../../../utils/ui/focus/FocusLoss";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, fontSize, InfoIconStyled, radius, zIndex} from "../../styles/styles";
import {InfoBubbleV5} from "../../button/InfoBubbleV5";
import ErrorInfoIcon from "../../icons/errorInfo.svg?react";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {ReactUtils} from "../../../utils/ReactUtils";

interface ITextInputProps {
	readonly isTextArea?: boolean;
	readonly defaultValue?: string;
	readonly value?: string;
	readonly placeholder?: string;
	readonly className?: string;
	readonly style?: React.CSSProperties;
	readonly autoFocus?: boolean;
	readonly disabled?: boolean;
	readonly readOnly?: boolean;
	readonly inputType?: IInputType;
	readonly trimText?: boolean; // trim the whitespace from the beginning and the end of the string if true
	readonly selectAll?: boolean;
	readonly blurOnEnter?: boolean;
	readonly borderColor?: string;
	readonly viewBoxSize?: number;
	readonly showErrorOnlyIfEdited?: boolean;
	readonly updateLastValidOnInput?: boolean;
	readonly divRef?: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
	readonly getErrorMessage?: (value: string) => string;
	readonly onChange?: (value: string) => void;
	readonly onBlur?: (isEnterPressed?: boolean) => void;
	readonly onInput?: (value: string, event?: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	readonly onClick?: (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	readonly onKeyDown?: (event: React.KeyboardEvent, value: string) => void;
}

export type IInputType = "number" | "date" | "text" | "password" | "search";

interface ITextInputState {
	value: string;
	isFocusingInfoIcon: boolean;
	hasBeenEditedSinceSave: boolean;
}

export class TextInputV5 extends React.Component<ITextInputProps, ITextInputState> {
	private readonly _ref = React.createRef<HTMLInputElement | HTMLTextAreaElement>();
	private readonly _infoIconRef = React.createRef<HTMLDivElement>();
	private _valueBeforeEditing: string = this.props.value;
	private _lastValidValue = this.props.value;
	private _isEscClicked: boolean = false;
	private _hasTriggeredChange: boolean = false;

	public static readonly defaultProps: ITextInputProps = {
		defaultValue: "",
		value: "",
		placeholder: "",
		trimText: true,
		showErrorOnlyIfEdited: true,
		inputType: "text",
		blurOnEnter: true,
		getErrorMessage: () => "",
		updateLastValidOnInput: true,
	};

	constructor(props: ITextInputProps) {
		super(props);

		const value = this.props.defaultValue || this.props.value;

		this.state = {
			value: value,
			isFocusingInfoIcon: true,
			hasBeenEditedSinceSave: false,
		};
	}

	private readonly onInput = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const {onInput, getErrorMessage, updateLastValidOnInput} = this.props;
		const value = event.currentTarget.value;
		const errorMessage = getErrorMessage?.(value) || "";

		this.setState({
			value,
			isFocusingInfoIcon: true,
			hasBeenEditedSinceSave: true,
		});
		this._hasTriggeredChange = false;

		onInput?.(value, event);

		if (!errorMessage && updateLastValidOnInput) {
			this._lastValidValue = value;
		}
	};

	private readonly onKeyUp = (event: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
		switch (event.key) {
			case KeyboardListener.KEY_ESCAPE:
				(this.props.divRef ?? this._ref).current.blur();
				break;
			case KeyboardListener.KEY_ENTER:
				if (this.props.blurOnEnter && (!this.props.isTextArea || !event.shiftKey)) {
					(this.props.divRef ?? this._ref).current.blur();
				}
				break;
		}
	};

	private readonly onBlur = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
		this.triggerChange(event);
		this.props.onBlur?.();

		this.setState({
			isFocusingInfoIcon: false,
		});

		return false;
	};

	private readonly triggerChange = (
		event?:
			| React.KeyboardEvent<HTMLInputElement>
			| React.KeyboardEvent<HTMLTextAreaElement>
			| React.ChangeEvent<HTMLInputElement>
			| React.ChangeEvent<HTMLTextAreaElement>,
		updateState: boolean = true,
	) => {
		const {onChange, getErrorMessage} = this.props;
		if (this._hasTriggeredChange) {
			return;
		}

		this._hasTriggeredChange = true;

		if (onChange) {
			const eventTargetValue = this.inputElementMaybe?.value ?? event?.currentTarget.value ?? this.state.value;
			const errorMessage = getErrorMessage(eventTargetValue);

			if (this._isEscClicked) {
				if (updateState) {
					this.setState({value: this._valueBeforeEditing});
				}
				onChange?.(this._valueBeforeEditing);
				this._isEscClicked = false;
			} else {
				if (eventTargetValue != null && !errorMessage) {
					const validateValue = this.validateValue(eventTargetValue);

					if (validateValue !== this.props.value) {
						onChange(validateValue);
						this._valueBeforeEditing = validateValue;
					}
				} else if (errorMessage) {
					const errorMessageWithPreviousValue = getErrorMessage(this._valueBeforeEditing);
					if (errorMessageWithPreviousValue) {
						onChange(this._lastValidValue);
						this._valueBeforeEditing = this._lastValidValue;
					} else {
						onChange(this._valueBeforeEditing);
						this._lastValidValue = this._valueBeforeEditing;
					}

					if (updateState) {
						this.setState({value: this._lastValidValue});
					}
				}
			}
		}
	};

	private readonly onKeyDown = (event: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === KeyboardListener.KEY_ESCAPE) {
			this._isEscClicked = true;
		}
		if (event.key === KeyboardListener.KEY_ENTER && this.props.isTextArea && !event.shiftKey) {
			event.preventDefault();
		}
		this.props.onKeyDown?.(event, this.state.value);
	};

	private readonly onClick = (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		this.props.onClick?.(e);
	};

	protected validateValue(value: string) {
		return this.props.trimText ? value.trim() : value;
	}

	private readonly turnFocusOn = () => {
		this.setState({isFocusingInfoIcon: true});
		FocusLoss.listen(this.inputElementMaybe, this.onBlur as any);
	};

	private readonly turnFocusOff = () => {
		this.setState({isFocusingInfoIcon: false});
		FocusLoss.stopListen(this.inputElementMaybe, this.onBlur as any);
	};

	private get inputElementMaybe() {
		return this.props.divRef?.current ?? this._ref.current;
	}

	public override async componentDidMount() {
		const {autoFocus, selectAll, isTextArea, value} = this.props;
		const input = this.inputElementMaybe;

		if (autoFocus) {
			// wait till input values are assgined before focus
			await TimeUtils.wait(0);
			input.focus();
		}

		if (selectAll) {
			input.select();
		}

		if (isTextArea) {
			const endOfString = value.length;
			input.setSelectionRange(endOfString, endOfString);
		}
	}

	public override UNSAFE_componentWillReceiveProps(nextProps: Readonly<ITextInputProps>, nextContext: any): void {
		if (this.props.value !== nextProps.value) {
			this.setState({
				value: nextProps.value,
			});

			if (!nextProps.onInput) {
				this.setState({
					hasBeenEditedSinceSave: false,
				});
			}
		}
	}

	public override componentWillUnmount() {
		this.triggerChange(undefined, false);

		FocusLoss.stopListen(this.inputElementMaybe, this.onBlur as any);
	}

	public override render() {
		const {
			disabled,
			placeholder,
			className,
			style,
			autoFocus,
			readOnly,
			borderColor,
			viewBoxSize,
			isTextArea,
			showErrorOnlyIfEdited,
			inputType,
			divRef,
			getErrorMessage,
		} = this.props;
		const {value, isFocusingInfoIcon, hasBeenEditedSinceSave} = this.state;
		const inputStyle: React.CSSProperties = {
			...style,
			width: viewBoxSize ?? style?.width,
			borderColor: borderColor ?? style?.borderColor,
		};

		const errorMessage = getErrorMessage(value);
		const showErrorMessage = (!showErrorOnlyIfEdited || (showErrorOnlyIfEdited && hasBeenEditedSinceSave)) && errorMessage && !disabled;

		if (isTextArea) {
			return (
				<TextAreaStyled
					value={value}
					placeholder={placeholder}
					onClick={this.onClick}
					className={className || ""}
					style={inputStyle}
					autoFocus={autoFocus}
					onInput={this.onInput}
					onBlur={this.onBlur}
					onKeyUp={this.onKeyUp}
					onKeyDown={this.onKeyDown}
					disabled={!!disabled}
					readOnly={!!readOnly}
					ref={(divRef as React.RefObject<HTMLTextAreaElement>) ?? (this._ref as React.RefObject<HTMLTextAreaElement>)}
				/>
			);
		} else {
			return (
				<TextInputWrapperStyled>
					<TextInputStyled
						value={value ?? ""}
						placeholder={placeholder}
						className={ReactUtils.cls(className || "", {disabled})}
						style={inputStyle || null}
						autoFocus={autoFocus}
						onInput={this.onInput}
						onBlur={this.onBlur}
						onClick={this.onClick}
						disabled={!!disabled}
						readOnly={!!readOnly}
						ref={(divRef as React.RefObject<HTMLInputElement>) ?? (this._ref as React.RefObject<HTMLInputElement>)}
						onKeyUp={this.onKeyUp}
						onKeyDown={this.onKeyDown}
						type={inputType}
						onFocus={this.turnFocusOn}
					/>
					{showErrorMessage && (
						<InfoIconStyled
							ref={this._infoIconRef}
							className={ReactUtils.cls("infoIcon", {isFocusingInfoIcon})}
						>
							<ErrorInfoIcon
								onMouseEnter={this.turnFocusOn}
								onMouseLeave={this.turnFocusOff}
							/>
							<InfoBubbleV5
								anchorRef={this._infoIconRef}
								content={errorMessage}
								isErrorMessage={true}
								hide={!isFocusingInfoIcon}
							/>
						</InfoIconStyled>
					)}
				</TextInputWrapperStyled>
			);
		}
	}
}

export const TextInputWrapperStyled = styled.div`
	position: relative;
	width: 100%;
`;

export const TextInputStyled = styled.input`
	background: none;
	width: 100%;
	height: 32px;
	border: 1px solid ${colorPalette.gray.c200Light};
	border-radius: ${radius.sm};
	padding: 0 ${baseDistance.sm};
	font-size: ${fontSize.md};
	line-height: 16px;
	color: ${colorPalette.gray.c950};

	&:focus {
		border-color: ${colorPalette.primary.c500Primary};
		outline: none;
	}

	&.disabled {
		cursor: initial;
		border: none;
	}
`;

// Preserve the existing textarea behavior without relying on legacy V4 global selectors.
const TextAreaStyled = styled.textarea`
	width: 100%;
	height: auto;
	border: solid 1px var(--input-border);
	padding: 11px;
	font-size: ${fontSize.md};
	line-height: 16px;
	color: var(--text1);
	resize: none;
	transition: all 0.2s ease;

	&:active,
	&:focus {
		background: var(--bg3);
		border: solid 1px var(--blue);
		outline: 0 none;
		z-index: ${zIndex.focus};
	}
`;
